import { Link } from 'react-router-dom'
import { TERMS_VERSION } from '../legal'

/**
 * The health and accuracy disclaimer, at the public `/disclaimer`.
 *
 * Accepted at sign-up alongside the privacy policy. It exists because the app shows heart rate
 * and a calorie figure, and those numbers invite more trust than they deserve — this says plainly
 * what they are and are not.
 */
export function DisclaimerPage() {
  return (
    <div className="page page-narrow legal">
      <div className="page-head">
        <div>
          <Link to="/" className="muted">
            ← Back
          </Link>
          <h1 style={{ marginTop: 6 }}>Disclaimer</h1>
          <div className="muted">Version {TERMS_VERSION} · My Tracker, by Highveld Tech (Pty) Ltd</div>
        </div>
      </div>

      <div className="card form-card">
        <h2>My Tracker is not a medical device</h2>
        <p>
          It is a training log. It is not a medical device, it is not certified as one, and nothing
          it shows is a diagnosis, a medical opinion or a reason to act on your health. Do not use
          it to detect, monitor or manage any medical condition.
        </p>
        <p>
          <strong>If you feel unwell, stop.</strong> Chest pain, dizziness, breathlessness beyond
          normal exertion — stop exercising and get medical help. Do not wait to see what the app
          says, and do not carry on because the numbers look acceptable.
        </p>
        <p>
          In an emergency, call emergency services. My Tracker cannot summon help, does not detect
          falls or incidents, and will not contact anyone on your behalf.
        </p>

        <h2>Talk to a doctor first</h2>
        <p>
          If you are new to exercise, returning after injury or illness, pregnant, or living with a
          heart, lung or metabolic condition, speak to a qualified professional before starting or
          changing a training routine. Nothing here is a substitute for that conversation.
        </p>

        <h2>The numbers are estimates</h2>
        <p>
          <strong>Calories</strong> are an approximation — roughly 1.036 kcal per kilogram of body
          mass per kilometre, against whatever weight is in your profile. If you have not set a
          weight, a placeholder of 70 kg is used. It takes no account of terrain, wind, heat,
          fitness or efficiency. Treat it as a rough trend, never as an intake target.
        </p>
        <p>
          <strong>Heart rate</strong> comes from whichever Bluetooth strap you pair. Its accuracy
          is the strap's, not ours. Straps lose contact, report badly when loose or dry, and drop
          out entirely. Gaps are marked honestly in your data rather than filled in with guesses.
        </p>
        <p>
          <strong>Distance, pace and elevation</strong> come from your phone's GPS. Accuracy varies
          with sky view, weather and the phone itself. Among buildings, under trees or indoors, a
          fix may be poor or absent — and a run recorded without a usable fix will show a distance
          far shorter than you actually covered. The Activity tab has a GPS check for exactly this
          reason; use it before you set out if the route matters.
        </p>
        <p>
          <strong>Cadence</strong> is derived from the phone's step counter when no footpod is
          paired, and is an approximation.
        </p>

        <h2>Pay attention to where you are</h2>
        <p>
          Watch the road, not the screen. You are responsible for your own safety, for obeying
          traffic laws, and for choosing routes and conditions that are sensible for you. Do not
          operate the app while crossing roads or anywhere that demands your attention.
        </p>

        <h2>Recording uses your battery</h2>
        <p>
          Continuous GPS and an active Bluetooth connection drain a battery quickly. A long run can
          leave you without a phone at the end of it. Plan accordingly, particularly if the phone
          is how you would call for help.
        </p>

        <h2>No guarantee of service or data</h2>
        <p>
          We work to keep My Tracker running and your runs safe, but we cannot guarantee the
          service will be uninterrupted or that data will never be lost. Export anything you cannot
          afford to lose — the <em>Download TCX</em> button gives you the original file of any run,
          and those files open in Garmin Connect, Strava and other tools.
        </p>

        <h2>Use at your own risk</h2>
        <p>
          My Tracker is provided as it is, without warranty of any kind. To the fullest extent
          permitted by law, Highveld Tech (Pty) Ltd is not liable for injury, loss or damage
          arising from your use of the app, from reliance on any figure it displays, or from data
          being unavailable, inaccurate or lost. Nothing here limits liability that cannot lawfully
          be limited.
        </p>
        <p>
          This disclaimer is governed by the laws of the Republic of South Africa.
        </p>

        <p className="muted" style={{ marginTop: 20 }}>
          See also the <Link to="/privacy">privacy policy</Link>.
        </p>
      </div>
    </div>
  )
}
